// lib/workspace-version-labels.ts
import { WorkspaceVersion } from '#types/types';

export const sortVersionsByTimeline = (left: WorkspaceVersion, right: WorkspaceVersion): number => {
  const leftTimestamp = Number.isFinite(left.timestamp) ? left.timestamp : 0;
  const rightTimestamp = Number.isFinite(right.timestamp) ? right.timestamp : 0;
  if (leftTimestamp !== rightTimestamp) {
    return leftTimestamp - rightTimestamp;
  }
  return left.id.localeCompare(right.id);
};

export const sortVersionsByRecency = (left: WorkspaceVersion, right: WorkspaceVersion): number =>
  sortVersionsByTimeline(right, left);

export const computeLineageFromFocus = (
  versions: WorkspaceVersion[] | undefined,
  targetVersionId: string | null | undefined
): WorkspaceVersion[] => {
  if (!Array.isArray(versions) || versions.length === 0) return [];
  if (!targetVersionId || targetVersionId.trim().length === 0) return [];

  const versionMap = new Map<string, WorkspaceVersion>();
  versions.forEach((version) => {
    versionMap.set(version.id, version);
  });

  const lineage: WorkspaceVersion[] = [];
  const visited = new Set<string>();
  let cursorId: string | null = targetVersionId;

  while (cursorId && !visited.has(cursorId)) {
    visited.add(cursorId);
    const version = versionMap.get(cursorId);
    if (!version) break;
    lineage.push(version);
    cursorId = version.parentId;
  }

  return lineage;
};

export const computeVersionHierarchyLabels = (
  versions: WorkspaceVersion[] | undefined,
  focusVersionId: string | null | undefined
): Map<string, string> => {
  const labels = new Map<string, string>();
  if (!Array.isArray(versions) || versions.length === 0) {
    return labels;
  }

  const orderedByTimeline = [...versions].sort(sortVersionsByTimeline);
  orderedByTimeline.forEach((version, index) => {
    labels.set(version.id, String(index + 1));
  });

  const versionById = new Map<string, WorkspaceVersion>();
  versions.forEach((version) => {
    versionById.set(version.id, version);
  });

  const normalizedFocusId =
    typeof focusVersionId === 'string' && focusVersionId.trim().length > 0 && versionById.has(focusVersionId)
      ? focusVersionId
      : (orderedByTimeline.at(-1)?.id ?? null);

  const lineage = computeLineageFromFocus(versions, normalizedFocusId);
  if (lineage.length === 0) {
    return labels;
  }

  const lineageIndexById = new Map<string, number>();
  lineage.forEach((version, index) => {
    lineageIndexById.set(version.id, index);
  });

  const getParentVersionId = (versionId: string): string | null => {
    const version = versionById.get(versionId);
    if (!version) return null;
    if (typeof version.parentId === 'string' && version.parentId.trim().length > 0) {
      return version.parentId;
    }
    return null;
  };

  const resolveFloorIndex = (versionId: string): number => {
    let cursorId: string | null = versionId;
    const visited = new Set<string>();
    while (cursorId && !visited.has(cursorId)) {
      visited.add(cursorId);
      const lineageIndex = lineageIndexById.get(cursorId);
      if (typeof lineageIndex === 'number') {
        return lineageIndex;
      }
      const parentId = getParentVersionId(cursorId);
      cursorId = parentId && versionById.has(parentId) ? parentId : null;
    }
    return 0;
  };

  const bucketsByFloorIndex = new Map<number, WorkspaceVersion[]>();
  versions.forEach((version) => {
    const floorIndex = resolveFloorIndex(version.id);
    const bucket = bucketsByFloorIndex.get(floorIndex) || [];
    bucket.push(version);
    bucketsByFloorIndex.set(floorIndex, bucket);
  });

  const totalFloors = lineage.length;
  bucketsByFloorIndex.forEach((bucket, floorIndex) => {
    const floorLabel = Math.max(1, totalFloors - floorIndex);
    const lineageAnchor = lineage[floorIndex] ?? null;
    const lineageAnchorId = lineageAnchor?.id ?? null;

    if (lineageAnchorId) {
      labels.set(lineageAnchorId, String(floorLabel));
    }

    const orderedNonAnchorVersions = bucket
      .filter((version) => version.id !== lineageAnchorId)
      .sort(sortVersionsByTimeline);

    orderedNonAnchorVersions.forEach((version, index) => {
      labels.set(version.id, `${floorLabel}.${index + 1}`);
    });
  });

  return labels;
};
